'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Calendar, MapPin, ChevronRight, Clock, DollarSign, Flag } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface RaceEvent {
  id: string;
  name: string;
  date: string;
  venue: string;
  city: string;
  format: string[];
  fee: number;
  status: string;
  round: number | null;
  season: number;
  organizer_name?: string;
  organizer_id?: string;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function UpcomingEventsPreview() {
  const supabase = createClient();
  const [upcoming, setUpcoming] = useState<RaceEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUpcoming = async () => {
      const { data } = await supabase
        .from('race_events')
        .select('id, name, date, venue, city, format, fee, status, round, season, organizer_name, organizer_id')
        .eq('status', 'upcoming')
        .order('date', { ascending: true })
        .limit(3);
      if (data) setUpcoming(data as RaceEvent[]);
      setLoading(false);
    };
    fetchUpcoming();
  }, []);

  if (loading) {
    return (
      <section className="py-16 md:py-20 px-4 lg:px-8 xl:px-10 max-w-screen-2xl mx-auto">
        <div className="flex items-center justify-center py-16">
          <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20 px-4 lg:px-8 xl:px-10 max-w-screen-2xl mx-auto">
      <div className="flex items-end justify-between mb-8 md:mb-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">2026 Season</p>
          <h2 className="text-2xl md:text-4xl font-extrabold uppercase tracking-tight text-foreground">Upcoming Races</h2>
        </div>
        <Link href="/events" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors">
          View All <ChevronRight size={16} />
        </Link>
      </div>

      {upcoming.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Flag size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-semibold text-muted-foreground">No upcoming races scheduled</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
          {upcoming.map((event) => (
            <div
              key={event.id}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-all duration-200 hover:-translate-y-0.5 group"
            >
              <div className="h-1 w-full bg-gradient-to-r from-primary to-accent" />

              <div className="p-4 md:p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    {event.round ? `Round ${event.round} / ${event.season}` : `${event.season} Season`}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-bold badge-upcoming">UPCOMING</span>
                </div>

                {/* Event name — clickable */}
                <Link href={`/events?event=${event.id}`} className="block mb-1">
                  <h3 className="text-sm md:text-base font-bold text-foreground leading-snug group-hover:text-primary transition-colors">
                    {event.name}
                  </h3>
                </Link>

                {/* Organizer name — clickable */}
                {event.organizer_name && (
                  <Link
                    href={event.organizer_id ? `/organizer-profile/${event.organizer_id}` : '/events'}
                    className="text-xs text-primary/80 hover:text-primary font-semibold transition-colors mb-3 block"
                  >
                    by {event.organizer_name}
                  </Link>
                )}

                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar size={13} className="text-primary shrink-0" />
                    {formatDate(event.date)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin size={13} className="text-primary shrink-0" />
                    <span className="truncate">{event.city}</span>
                  </div>
                  {event.format && event.format.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock size={13} className="text-primary shrink-0" />
                      <span className="truncate">{event.format[0]} → {event.format[event.format.length - 1]}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <DollarSign size={13} className="text-primary shrink-0" />
                    RM {event.fee} entry fee
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Link href={`/event-registration?event=${event.id}`} className="flex-1 text-center py-2 text-sm font-bold uppercase tracking-wide rounded-md btn-primary">
                    Register
                  </Link>
                  <Link href={`/events?event=${event.id}`} className="px-3 py-2 text-sm font-bold rounded-md btn-secondary">
                    Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex sm:hidden justify-center mt-6">
        <Link href="/events" className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors">
          View All Events <ChevronRight size={16} />
        </Link>
      </div>
    </section>
  );
}